import { useCallback, useMemo, useRef } from "react";

type UseStableListProps<T> = {
	list: T[];
	onShift: (oldIndex: number, newIndex: number) => void;
};

type StableItem<T> = T extends object ? T & { _id: string } : { value: T; _id: string };

type UseStableListResult<T> = {
	stableList: StableItem<T>[];
	shiftItems: (oldIndex: number, newIndex: number) => void;
	removeId: (index: number) => void;
	addId: () => void;
};

/**
 * Hook to maintain stable IDs for list items that may not have unique identifiers.
 * Useful for drag-and-drop lists where items need consistent keys across renders.
 *
 * This hook:
 * - Generates and maintains stable UUIDs for each item in the list
 * - Provides helpers to keep IDs synchronized with list operations
 * - Handles additions, removals, and reordering
 *
 * @template T - The type of items in the list (can be primitives or objects)
 *
 * @example
 * ```tsx
 * const [values, setValues] = useState(["Apple", "Banana", "Orange"]);
 * const { stableList, shiftIds } = useStableList({ list: values });
 *
 * // stableList: [
 * //   { _id: "uuid-1", value: "Apple" },
 * //   { _id: "uuid-2", value: "Banana" },
 * //   { _id: "uuid-3", value: "Orange" }
 * // ]
 *
 * <SortableDragNDrop
 *   items={stableList}
 *   onDragEnd={(event) => {
 *     const oldIndex = stableList.findIndex(item => item._id === event.active.id);
 *     const newIndex = stableList.findIndex(item => item._id === event.over?.id);
 *
 *     // Update form/state
 *     field.shiftValue(oldIndex, newIndex);
 *
 *     // Keep IDs synchronized with the shift
 *     shiftIds(oldIndex, newIndex);
 *   }}
 * />
 * ```
 */
const useStableList = <T,>({ list, onShift }: UseStableListProps<T>): UseStableListResult<T> => {
	// Store stable IDs that persist across renders
	const idsRef = useRef<string[]>([]);

	// Grow ID array as list grows (add new IDs for new items)
	while (idsRef.current.length < list.length) {
		idsRef.current.push(crypto.randomUUID());
	}

	// Shrink ID array as list shrinks
	if (idsRef.current.length > list.length) {
		idsRef.current = idsRef.current.slice(0, list.length);
	}

	// Build stable list with IDs
	const stableList = useMemo(
		() =>
			list.map((value, idx) => {
				if (typeof value === "object" && value !== null) {
					return { ...value, _id: idsRef.current[idx] } as StableItem<T>;
				} else {
					// For primitives, wrap in an object
					return { value, _id: idsRef.current[idx] } as StableItem<T>;
				}
			}),
		[list]
	);

	/**
	 * Shift an ID from one position to another.
	 * Call this after shifting values in your form/state to keep IDs synchronized.
	 *
	 * @param oldIndex - The original index of the item
	 * @param newIndex - The new index of the item
	 */
	const shiftItems = useCallback(
		(oldIndex: number, newIndex: number) => {
			onShift(oldIndex, newIndex);
			const newIds = [...idsRef.current];
			const [movedId] = newIds.splice(oldIndex, 1);
			newIds.splice(newIndex, 0, movedId);
			idsRef.current = newIds;
		},
		[onShift]
	);

	/**
	 * Remove an ID at a specific index.
	 * Call this after removing a value from your list to keep IDs synchronized.
	 *
	 * @param index - The index of the item to remove
	 */
	const removeId = useCallback((index: number) => {
		const newIds = [...idsRef.current];
		newIds.splice(index, 1);
		idsRef.current = newIds;
	}, []);

	/**
	 * Add a new ID to the end of the list.
	 * Call this after adding a new value to your list to keep IDs synchronized.
	 * Note: The hook automatically grows the ID array to match list length,
	 * so you may not need to call this manually in most cases.
	 */
	const addId = useCallback(() => {
		idsRef.current.push(crypto.randomUUID());
	}, []);

	return { stableList, shiftItems, removeId, addId };
};

export default useStableList;
export type { StableItem, UseStableListResult };
